import { setInteractionError } from '@atlaskit/react-ufo/set-interaction-error';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { expVal } from '@atlassian/jira-feature-experiments';
import { transformIssueArisToIds } from '../../utils/transform-issue-aris-to-id/index.tsx';
import {
	SubmitFlagStatus,
	type FormSubmitState,
	type IssueUpdateCallbackPayload,
	type IssueUpdateFlagData,
} from './types.tsx';

export const actions = {
	startSubmit:
		(interactionSource?: string): Action<FormSubmitState> =>
		({ setState }) => {
			setState({
				flagStatus: SubmitFlagStatus.NoFlag,
				data: undefined,
				interactionSource,
			});
		},
	setIdle:
		(): Action<FormSubmitState> =>
		({ setState }) => {
			setState({
				flagStatus: SubmitFlagStatus.NoFlag,
				data: undefined,
			});
		},
	dismissPollingFlag:
		(): Action<FormSubmitState> =>
		({ setState, getState }) => {
			const { flagStatus, data } = getState();
			if (flagStatus === SubmitFlagStatus.ProgressFlag) {
				setState({
					flagStatus: SubmitFlagStatus.NoFlag,
					flagData: undefined,
					data,
				});
			}
		},
	setPollingStart:
		(
			taskId: string,
			inputData: IssueUpdateCallbackPayload[],
			flagData?: IssueUpdateFlagData,
		): Action<FormSubmitState> =>
		({ setState, getState }) => {
			const { callbacks } = getState();
			callbacks.onUpdateStart?.(
				inputData.map(({ issueIds, data, bulkOperationType }) => ({
					data,
					issueIds: transformIssueArisToIds(issueIds),
					bulkOperationType: expVal('bulk_operations_in_nin_experiment', 'isEnabled', false)
						? bulkOperationType
						: undefined,
				})),
			);
			setState({
				flagStatus: SubmitFlagStatus.ProgressFlag,
				flagData,
				data: {
					taskId,
				},
			});
		},
	/*
		This action is a temporary workaround to open the legacy bulk edit page.
		The Bulk Transition doesn't have support to return the issueKeys so this implementation was done here
		and will be removed when we implement Bulk Transition with multiple workflows.
		Project Plan: https://hello.atlassian.net/wiki/spaces/JSDee/pages/3388755384/Bulk+Transition+Project+Plan
		*/
	redirectToLegacyBulkTransition:
		(): Action<FormSubmitState> =>
		({ getState }) => {
			const { callbacks, issueIds } = getState();
			callbacks.onLegacyBulkEditRequest?.(issueIds);
		},
	updateProgress:
		(progress: number): Action<FormSubmitState> =>
		({ setState, getState }) => {
			const { flagStatus, data } = getState();
			if (flagStatus !== SubmitFlagStatus.ProgressFlag) {
				return;
			}
			setState({
				flagStatus: SubmitFlagStatus.ProgressFlag,
				data: {
					progress,
					taskId: data.taskId,
				},
			});
		},
	setError:
		(): Action<FormSubmitState> =>
		({ setState, getState }) => {
			const { callbacks, issueIds, interactionSource } = getState();
			if (interactionSource) {
				setInteractionError(interactionSource, {
					name: 'errorSubmitBulkEdit',
					errorMessage: `Error submitting changes for issues:${issueIds}`,
				});
			}
			callbacks.onUpdateError?.(issueIds);
			setState({
				flagStatus: SubmitFlagStatus.ErrorFlag,
				data: {},
			});
		},
	setPartialError:
		(
			totalCount: number,
			failedCount: number,
			failedIssueKeys: string[],
			failedIssueIds: number[],
		): Action<FormSubmitState> =>
		({ setState, getState }) => {
			const { callbacks } = getState();
			callbacks.onUpdateError?.(failedIssueIds.map(Number));
			setState({
				flagStatus: SubmitFlagStatus.PartialErrorFlag,
				data: { totalCount, failedCount, issueKeys: failedIssueKeys },
			});
		},
	setSuccess:
		(numberOfIssuesUpdated: number): Action<FormSubmitState> =>
		({ setState, getState }) => {
			const { callbacks, issueIds } = getState();
			callbacks.onUpdateSuccess?.(issueIds);
			setState({
				flagStatus: SubmitFlagStatus.SuccessFlag,
				data: {
					numberOfIssuesUpdated,
				},
			});
		},
} as const;

export type Actions = typeof actions;
